import React from 'react';
import { TestResult } from '../services/resultService';
import { Question } from '../utils/questionService';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Radar, 
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import psychometrics from '../data/psychometrics.json';

interface ScoreChartProps {
  results: TestResult;
  questionsData: Question[];
}

const ScoreChart: React.FC<ScoreChartProps> = ({ results, questionsData }) => {
  console.log('ScoreChart received:', { scores: results.scores, questionsData: !!questionsData });
  
  if (!results || typeof results !== 'object') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-600">Ошибка: данные оценок не найдены</p>
      </div>
    );
  }

  // Динамический список шкал по ключам из scores
  let allScales = Object.keys(results.scores);
  // Берём descriptions из psychometrics.scales
  const scalesData = psychometrics.scales || {};

  // --- Вычисляем min/max для всех баллов ---
  const allValues = allScales.map(scale => Math.round(results.scores[scale] || 0));
  const minValue = Math.min(...allValues, 0);
  const maxValue = Math.max(...allValues, 100);
  const radarDomain = minValue < 0 ? [minValue, maxValue] : [0, maxValue < 100 ? 100 : maxValue];

  let radarData = allScales.map(scale => ({
    scale: scalesData[scale]?.name || scale,
    value: Math.round(results.scores[scale] || 0),
    fullMark: 100
  }));

  let barData = allScales.map(key => ({
    name: scalesData[key]?.name || key,
    score: Math.round(results.scores[key] || 0),
    description: scalesData[key]?.description || ''
  }));

  // if (sortByName) { // Removed as per new_code
  //   barData = barData.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  //   radarData = radarData.sort((a, b) => a.scale.localeCompare(b.scale, 'ru'));
  // }

  const CustomTooltip: React.FC<{ active?: boolean; payload?: any; label?: string }> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          <p className="text-blue-600">{`Балл: ${payload[0].value}`}</p>
          {data.description && (
            <p className="text-sm text-gray-600 mt-1">{data.description}</p>
          )}
        </div>
      );
    }
    return null;
  };

  // --- Для тестов: если jsdom, используем фиксированные размеры ---
  const isJSDOM = typeof window !== 'undefined' && window.navigator && window.navigator.userAgent && window.navigator.userAgent.includes('jsdom');

  return (
    <div className="space-y-8">
      {/* Radar Chart (Recharts) */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Профиль личности (радарная диаграмма)
        </h3>
        <div className="h-96" style={{ minWidth: 320, minHeight: 320 }}>
          {isJSDOM ? (
            <RadarChart data={radarData} width={400} height={400}>
              <PolarGrid />
              <PolarAngleAxis 
                dataKey="scale" 
                tick={{ fontSize: 12 }}
                className="fill-gray-600"
              />
              <PolarRadiusAxis 
                angle={0}
                domain={radarDomain}
                tick={{ fontSize: 10 }}
                className="fill-gray-400"
              />
              {minValue < 0 && (
                <Radar
                  dataKey={() => 0}
                  stroke="#888"
                  fillOpacity={0}
                  isAnimationActive={false}
                />
              )}
              <Radar
                name="Баллы"
                dataKey="value"
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.3}
                strokeWidth={2}
              />
            </RadarChart>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid />
                <PolarAngleAxis 
                  dataKey="scale" 
                  tick={{ fontSize: 12 }}
                  className="fill-gray-600"
                />
                <PolarRadiusAxis 
                  angle={0}
                  domain={radarDomain}
                  tick={{ fontSize: 10 }}
                  className="fill-gray-400"
                />
                {minValue < 0 && (
                  <Radar
                    dataKey={() => 0}
                    stroke="#888"
                    fillOpacity={0}
                    isAnimationActive={false}
                  />
                )}
                <Radar
                  name="Баллы"
                  dataKey="value"
                  stroke="#3B82F6"
                  fill="#3B82F6"
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
              </RadarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Bar Chart */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Детальные баллы по шкалам
        </h3>
        <div className="h-96" style={{ minWidth: 320, minHeight: 320 }}>
          {isJSDOM ? (
            <BarChart
              data={barData}
              width={400}
              height={400}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 60,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
                tick={{ fontSize: 11 }}
              />
              <YAxis domain={radarDomain} />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="score" 
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={barData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 60,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                  tick={{ fontSize: 11 }}
                />
                <YAxis domain={radarDomain} />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="score" 
                  fill="#3B82F6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Score Table */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Таблица результатов
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-2 text-left">Шкала</th>
                <th className="border border-gray-300 px-4 py-2 text-center">Балл</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Описание</th>
                <th className="border border-gray-300 px-4 py-2 text-center">Уровень</th>
              </tr>
            </thead>
            <tbody>
              {barData.map(item => {
                const level = item.score <= 30 ? 'Низкий' : 
                             item.score <= 70 ? 'Средний' : 'Высокий';
                const levelColor = item.score <= 30 ? 'text-green-600' :
                                  item.score <= 70 ? 'text-yellow-600' : 'text-red-600';
                
                return (
                  <tr key={item.name} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 font-medium">
                      {item.name}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center font-bold">
                      {item.score}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-sm">
                      {item.description}
                    </td>
                    <td className={`border border-gray-300 px-4 py-2 text-center font-medium ${levelColor}`}>
                      {level}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ScoreChart;